import React from 'react';
import { Box, Container, Heading, Text, VStack, Link, ListItem, OrderedList } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { GIVEAWAY } from './comforterGiveaway';

const Section = ({ title, children }) => (
    <Box>
        <Heading as="h2" size="sm" mb={2} color="gray.800">{title}</Heading>
        <Text color="gray.700" fontSize="sm" lineHeight="tall">{children}</Text>
    </Box>
);

export default function ComforterGiveawayRulesPage() {
    const { laundryId } = useParams();
    const pageHref = laundryId ? `/${laundryId}/comforter-cleaning` : '/comforter-cleaning';
    const g = GIVEAWAY;

    return (
        <Box bg="gray.50" minH="100vh" py={{ base: 10, md: 16 }}>
            <Helmet>
                <title>Comforter Giveaway Official Rules | {g.sponsor}</title>
                <meta name="robots" content="noindex" />
            </Helmet>
            <Container maxW="760px" bg="white" borderRadius="2xl" boxShadow="sm" p={{ base: 6, md: 10 }}>
                <VStack align="stretch" spacing={6}>
                    <Box>
                        <Text fontSize="sm" color="teal.700" fontWeight="700" textTransform="uppercase" letterSpacing="0.1em">Official Rules</Text>
                        <Heading as="h1" size="lg" mt={1} color="gray.900">{g.sponsor} Comforter Giveaway</Heading>
                        <Text mt={3} fontWeight="700" color="gray.800">
                            NO PURCHASE NECESSARY TO ENTER OR WIN. A PURCHASE WILL NOT INCREASE YOUR CHANCES OF WINNING.
                            VOID WHERE PROHIBITED.
                        </Text>
                    </Box>

                    <OrderedList spacing={5} pl={2} styleType="none" m={0}>
                        <ListItem><Section title="1. Sponsor">
                            {g.sponsor}, {g.sponsorAddress}, {g.sponsorPhone} (“Sponsor”).
                        </Section></ListItem>

                        <ListItem><Section title="2. Eligibility">
                            Open to legal residents of Texas who are 18 years of age or older at the time of entry.
                            Employees of Sponsor and members of their immediate families or households are not eligible.
                        </Section></ListItem>

                        <ListItem><Section title="3. Entry period">
                            The giveaway begins {g.startLabel} and ends {g.endLabel} (the “Entry Period”). Entries received
                            outside the Entry Period will not be accepted.
                        </Section></ListItem>

                        <ListItem><Section title="4. How to enter">
                            Complete the entry form on the <Link href={pageHref} color="teal.600">comforter cleaning page</Link> with
                            your first name and a valid U.S. mobile phone number and agree to these Official Rules. Email and ZIP
                            code are optional. Limit one (1) entry per person and per phone number; additional entries will be
                            disregarded. Agreeing to receive marketing text messages is optional and is not required to enter or win.
                            Automated, scripted or bulk entries are void.
                        </Section></ListItem>

                        <ListItem><Section title="5. Prize">
                            One (1) winner will receive {g.prizeDetail.charAt(0).toLowerCase() + g.prizeDetail.slice(1)}, beginning
                            the month after the winner is confirmed. Approximate retail value: {g.prizeValue}. Each monthly cleaning
                            must be used in its month and does not carry over. Pickup and delivery fees, if any, are not included;
                            the winner may drop off at Sponsor’s store at no charge. The prize is not transferable and has no cash
                            value. No substitution except by Sponsor, who may substitute a prize of equal or greater value.
                        </Section></ListItem>

                        <ListItem><Section title="6. Drawing and odds">
                            The winner will be selected in a random drawing from all eligible entries {g.drawLabel}.
                            Odds of winning depend on the number of eligible entries received.
                        </Section></ListItem>

                        <ListItem><Section title="7. Winner notification">
                            The potential winner will be contacted by phone call or text message at the number provided within three
                            (3) days of the drawing and must respond within seven (7) days. If the potential winner cannot be reached,
                            does not respond, or is found ineligible, the prize may be forfeited and an alternate winner drawn.
                            The winner may be asked to show photo ID matching the entry.
                        </Section></ListItem>

                        <ListItem><Section title="8. Publicity">
                            Except where prohibited, the winner agrees that Sponsor may use their first name and city in announcing the
                            winner, without further compensation.
                        </Section></ListItem>

                        <ListItem><Section title="9. Privacy">
                            Information collected is used to administer this giveaway and as described in our{' '}
                            <Link href="/privacy-policy" color="teal.600">Privacy Policy</Link>. We will only send marketing text
                            messages to entrants who separately opt in. Message and data rates may apply; reply STOP to opt out at any time.
                        </Section></ListItem>

                        <ListItem><Section title="10. General conditions">
                            By entering, entrants agree to these Official Rules and Sponsor’s decisions, which are final. Sponsor is not
                            responsible for lost, late, incomplete or misdirected entries or technical failures. Sponsor may cancel,
                            modify or suspend the giveaway if fraud or technical problems affect its fairness. Winner is responsible
                            for any taxes. This giveaway is governed by the laws of the State of Texas.
                        </Section></ListItem>

                        <ListItem><Section title="11. Winner’s name">
                            To request the winner’s first name, contact Sponsor at {g.sponsorPhone} or visit the store after the drawing.
                        </Section></ListItem>
                    </OrderedList>

                    <Link href={pageHref} color="teal.600" fontWeight="600">← Back to the giveaway</Link>
                </VStack>
            </Container>
        </Box>
    );
}
